import type { EntityTableFilterParams } from '@/components/map/utils/entityMapFilters';

import { apiClient } from './apiClient';

/**
 * Repeat array params (`leakEntityStatuses=a&leakEntityStatuses=b`) rather than axios's
 * default bracketed form, which FastEndpoints' List binding does not read — the same
 * serializer the Network Table client uses for the identical filter params.
 */
const REPEAT_ARRAY_PARAMS = { indexes: null } as const;

export interface NetworkStats {
    leaksFound: number;
    noDigFixes: number;
    totalFixed: number;
    totalPoisGenerated: number;
    customerSideLeakageCount: number;
    suitableForNoDigCount: number;
    mainsPipesCount: number;
    mainsFittingsCount: number;
    communicationPipesCount: number;
    stopTapBoundaryBoxCount: number;
    assessedPoisCount: number;
    inconclusivePoisCount: number;
    alreadyMarkedUpPoisCount: number;
    unableToCheckPoisCount: number;
    awaitingAssessmentPoisCount: number;
    waterSaved: number | null;
    /** "Volume Identified" (litres/day) — rendered by the stats bar AND the selection cards. */
    potentialSaving: number | null;
    /**
     * Distinct client networks that contributed to the figures. Only the estate-wide
     * Network Explorer stats carry it; a single client's stats never do.
     */
    totalNetworks?: number;
}

export type StatsPeriod = 'overall' | 'thirty';

export async function fetchNetworkStats(
    clientId: string,
    period: StatsPeriod,
    filters?: EntityTableFilterParams | null,
    signal?: AbortSignal,
): Promise<NetworkStats> {
    const response = await apiClient.get<NetworkStats>('/network/stats', {
        params: { clientId, period, ...(filters ?? {}) },
        paramsSerializer: REPEAT_ARRAY_PARAMS,
        signal,
    });
    return response.data;
}

/**
 * Full network-stats-shaped totals scoped to a single Area of Interest
 * (`GET /network/stats/area/{areaName}`). Mirrors `fetchNetworkStats` counting
 * rules and period support so per-stat percentages of the network total stay
 * consistent. 404s when the area does not exist for the client.
 */
export async function fetchAreaNetworkStats(
    clientId: string,
    areaName: string,
    period: StatsPeriod,
    filters?: EntityTableFilterParams | null,
    signal?: AbortSignal,
): Promise<NetworkStats> {
    const response = await apiClient.get<NetworkStats>(`/network/stats/area/${encodeURIComponent(areaName)}`, {
        params: { clientId, period, ...(filters ?? {}) },
        paramsSerializer: REPEAT_ARRAY_PARAMS,
        signal,
    });
    return response.data;
}

/**
 * Full network-stats-shaped totals scoped to a single DMA
 * (`GET /network/stats/dma/{ident}`). See `fetchAreaNetworkStats`.
 */
export async function fetchDmaNetworkStats(
    clientId: string,
    ident: string,
    period: StatsPeriod,
    filters?: EntityTableFilterParams | null,
    signal?: AbortSignal,
): Promise<NetworkStats> {
    const response = await apiClient.get<NetworkStats>(`/network/stats/dma/${encodeURIComponent(ident)}`, {
        params: { clientId, period, ...(filters ?? {}) },
        paramsSerializer: REPEAT_ARRAY_PARAMS,
        signal,
    });
    return response.data;
}
